import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import {
  Package,
  AlertTriangle,
  Users,
  TrendingUp,
  DollarSign,
  CreditCard,
} from "lucide-react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Paper,
} from "@mui/material";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      // Laravel retorna { summary: {...} }
      setStats(response.data.summary || response.data);
    } catch (error) {
      console.error("Erro ao buscar estatísticas", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: 240,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const statCards = [
    {
      label: "Total de Produtos",
      value: stats?.total_products || 0,
      icon: Package,
      color: "#0878c7",
      bg: "rgba(16,151,230,0.12)",
    },
    {
      label: "Produtos Abaixo do Mínimo",
      value: stats?.low_stock_products || 0,
      icon: AlertTriangle,
      color: "#f59e0b",
      bg: "#fef3c7",
    },
    {
      label: "Total de Clientes",
      value: stats?.total_customers || 0,
      icon: Users,
      color: "#0c2545",
      bg: "#e8eef5",
    },
    {
      label: "Vendas Hoje",
      value: stats?.today_sales || 0,
      icon: TrendingUp,
      color: "#10b981",
      bg: "#d1fae5",
    },
    {
      label: "Receita Hoje",
      value: `R$ ${(stats?.today_revenue || 0)}`,
      icon: DollarSign,
      color: "#1097e6",
      bg: "rgba(85,199,255,0.18)",
    },
    {
      label: "Caixas Abertos",
      value: stats?.open_cashiers || 0,
      icon: CreditCard,
      color: "#0878c7",
      bg: "#e0f2fe",
    },
  ];

  return (
    <Box data-testid="dashboard-page">
      {/* CARDS – layout igual ao original (grid manual) */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(1, minmax(0, 1fr))",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
          },
          gap: 3,
          mb: 4,
        }}
      >
        {statCards.map((card, idx) => {
          const Icon = card.icon;
          return (
            <Card
              key={idx}
              data-testid={`stat-card-${idx}`}
              sx={{
                borderRadius: 1,
                border: "1px solid #dbe5ef",
                boxShadow: "0 14px 34px rgba(7,26,51,0.06)",
                minHeight: 180,
                background:
                  "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                  py: 3,
                }}
              >
                <Box>
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {card.label}
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{ fontWeight: 700 }}
                    data-testid={`stat-value-${idx}`}
                  >
                    {card.value}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: card.bg,
                    color: card.color,
                    flexShrink: 0,
                  }}
                >
                  <Icon size={28} />
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {/* RESUMO FINANCEIRO */}
      <Paper
        className="data-table-container"
        sx={{
          borderRadius: 1,
          boxShadow: "0 14px 34px rgba(7,26,51,0.06)",
        }}
      >
        <Box
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            px: 3,
            py: 2.5,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Resumo Financeiro
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 4,
            }}
          >
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Contas a Receber
              </Typography>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: "#10b981" }}
                data-testid="receivables-value"
              >
                R$ {(stats?.pending_receivables || 0)}
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Contas a Pagar
              </Typography>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: "#ef4444" }}
                data-testid="payables-value"
              >
                R$ {(stats?.pending_payables || 0)}
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Saldo
              </Typography>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: "#1097e6" }}
                data-testid="balance-value"
              >
                R$
                {" "}
                {(stats?.pending_receivables || 0) -
                  (stats?.pending_payables || 0)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
